
import Component from 'vue-class-component';
import SecondaryLineBarChartFilterable from '@/components/reports-v3/components/filterables/SecondaryLineBarChartFilterable.vue';
import {PermissionsGroup} from '@/store/models.def';
import echarts from 'echarts';
import gdbx from '@/store/modules/gdbx';
import {DataViewFormatter} from '@/components/reports-v3/components/elements/charts/helpers/dataViewFormatter';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import { COL, getTableName } from '@/worker/db/sqlHelpers';
import moment from 'moment/moment';
import {GobiColor} from '@/helpers/color';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';
import settingx from '@/store/modules/settingx';

@Component({
  components: {
    IncludeDebitNoteToggle,
    IncludeCreditNoteToggle,
    SlPhModuleOnlyToggle,
    SecondaryLineBarChartFilterable,
  },
})
export default class SalesTotalSalesComparisonByIndustryBarChart extends FilterWidget {
  public get permissionIds(): PermissionsGroup[] {
    return ['sales', 'customers'];
  }

  public dataZoom = [
    {
      type: 'slider',
    },
    {
      type: 'inside',
    },
  ];

  public filterIds: Array<
      | 'dateAsOf'
      | 'dateRange'
      | 'stockItems'
      | 'agents'
      | 'customers'
      | 'suppliers'
      | 'compareDate1'
      | 'compareDate2'
      | 'comparisonPeriod'
  > = [
    'customers',
    'agents',
    'compareDate1',
    'compareDate2',
    'comparisonPeriod',
  ];

  public xAxisData: string[] = [];
  public series: echarts.ECharts[] | any = [];

  public get currencySymbol() {
    return gdbx.currencySymbol;
  }

  public get yAxisLabel2() {
    let label: string = '';

    label = this.currencySymbol;

    return label;
  }

  public get dataViewFormatter() {
    return DataViewFormatter.salesTracker;
  }

  public get includeCreditNote() {
    return settingx.now.includeCreditNote;
  }

  public get includeDebitNote() {
    return settingx.now.includeDebitNote;
  }

  public get slPhModuleOnly() {
    return settingx.now.slPhModuleOnly;
  }

  public get dataDateRange() {
    return [
        moment(this.selectedDateRange[0]).subtract(1, 'year').valueOf(),
        this.selectedDateRange[1],
    ];
  }

  public get expensiveHook() {
    const {
      selectedDateRange,
      includeDebitNote,
      includeCreditNote,
      slPhModuleOnly,
    } = this;
    return JSON.stringify([
      selectedDateRange,
      includeDebitNote,
      includeCreditNote,
      slPhModuleOnly,
    ]);
  }

  public async expensiveCalc() {
    const jobId = this.currentJobId;
    this.generateLoadingText(0.2, 0.35);

    const queryHead = await this._ensureTablesLoaded(
      ['globalCompanyCategory', 'sales', 'creditNotes', 'customerTrades'] as any[],
    );
    this.checkInterrupted(jobId);

    const ccTable = getTableName('globalCompanyCategory');
    const salesTable = getTableName('sales');
    const cnTable = getTableName('creditNotes');
    const arTable = getTableName('customerTrades');

    const ccRows = await queryHead.rawQuery(`
      SELECT "CODE" as code, "DESCRIPTION" as descr FROM ${ccTable}
    `);
    const ccMap = new Map<string, string>();
    for (const r of ccRows) { ccMap.set(String(r.code), String(r.descr)); }
    this.checkInterrupted(jobId);
    this.generateLoadingText(0.35, 0.9);

    const pastYearDateRange: [number, number] = [
      moment(this.selectedDateRange[0]).subtract(1, 'year').valueOf(),
      moment(this.selectedDateRange[1]).subtract(1, 'year').valueOf(),
    ];

    const tyDateFilter = `TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${this.selectedDateRange[0]} AND ${this.selectedDateRange[1]}`;
    const pyDateFilter = `TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${pastYearDateRange[0]} AND ${pastYearDateRange[1]}`;

    const thisYearByCC: Array<{ code: string; text: string; sum: number }> = [];
    const pastYearByCC: Array<{ code: string; text: string; sum: number }> = [];

    if (this.slPhModuleOnly) {
      const docTypes = this.includeDebitNote ? ['IV', 'CS', 'DN'] : ['IV', 'CS'];
      const docTypeFilter = ` AND ${COL.DOCTYPE} IN (${docTypes.map((t) => `'${t}'`).join(', ')})`;

      const ccQueryForTable = (table: string, filter: string) => `
        SELECT "COMPANYCATEGORY" as cc, SUM(TRY_CAST("AMOUNT" AS DOUBLE)) as totalAmt
        FROM ${table}
        WHERE ${filter} GROUP BY "COMPANYCATEGORY"
      `;
      const [tySales, pySales, tyCN, pyCN] = await Promise.all([
        queryHead.rawQuery(ccQueryForTable(salesTable, `${tyDateFilter}${docTypeFilter}`)),
        queryHead.rawQuery(ccQueryForTable(salesTable, `${pyDateFilter}${docTypeFilter}`)),
        queryHead.rawQuery(ccQueryForTable(cnTable, tyDateFilter)),
        queryHead.rawQuery(ccQueryForTable(cnTable, pyDateFilter)),
      ]);

      const toMap = (rows: any[]) => {
        const m = new Map<string, number>();
        for (const r of rows) { m.set(String(r.cc), Number(r.totalAmt) || 0); }
        return m;
      };
      const tySalesMap = toMap(tySales);
      const pySalesMap = toMap(pySales);
      const tyCNMap = toMap(tyCN);
      const pyCNMap = toMap(pyCN);

      const allCCs = new Set([...tySalesMap.keys(), ...pySalesMap.keys()]);
      for (const cc of allCCs) {
        if (!cc) { continue; }
        const text = ccMap.get(cc) || cc;
        const tyS = tySalesMap.get(cc) || 0;
        const tyC = this.includeCreditNote ? (tyCNMap.get(cc) || 0) : 0;
        const pyS = pySalesMap.get(cc) || 0;
        const pyC = this.includeCreditNote ? (pyCNMap.get(cc) || 0) : 0;
        thisYearByCC.push({ code: cc, text, sum: tyS - tyC });
        pastYearByCC.push({ code: cc, text, sum: pyS - pyC });
      }
    } else {
      const arQueryForFilter = (filter: string) => `
        SELECT "COMPANYCATEGORY" as cc,
          SUM(CASE WHEN "DOCTYPE" = 'IV' THEN TRY_CAST("LOCALAMOUNT" AS DOUBLE) ELSE 0 END) as ivAmt,
          SUM(CASE WHEN "DOCTYPE" = 'DN' THEN TRY_CAST("LOCALAMOUNT" AS DOUBLE) ELSE 0 END) as dnAmt,
          SUM(CASE WHEN "DOCTYPE" = 'CN' THEN TRY_CAST("LOCALAMOUNT" AS DOUBLE) ELSE 0 END) as cnAmt
        FROM ${arTable}
        WHERE ${filter} GROUP BY "COMPANYCATEGORY"
      `;
      const [tyAR, pyAR] = await Promise.all([
        queryHead.rawQuery(arQueryForFilter(tyDateFilter)),
        queryHead.rawQuery(arQueryForFilter(pyDateFilter)),
      ]);

      const processAR = (rows: any[], target: typeof thisYearByCC) => {
        for (const r of rows) {
          const cc = String(r.cc);
          if (!cc) { continue; }
          const iv = Number(r.ivAmt) || 0;
          const dn = this.includeDebitNote ? (Number(r.dnAmt) || 0) : 0;
          const cn = this.includeCreditNote ? (Number(r.cnAmt) || 0) : 0;
          target.push({ code: cc, text: ccMap.get(cc) || cc, sum: iv + dn - cn });
        }
      };
      processAR(tyAR, thisYearByCC);
      processAR(pyAR, pastYearByCC);
    }
    this.generateLoadingText(0.95);

    thisYearByCC.sort((a, b) => b.sum - a.sum);

    const pastYearMap = new Map<string, number>();
    for (const p of pastYearByCC) { pastYearMap.set(p.code, p.sum); }

    this.xAxisData = thisYearByCC.map((dd) => dd.text);

    this.series = [
      {
        name: 'This Year', type: 'bar', smooth: true,
        barWidth: '40%', color: GobiColor.COLOR1,
        data: thisYearByCC.map((dd) => dd.sum),
      },
      {
        name: 'Last Year', type: 'bar', smooth: true,
        barWidth: '40%', color: GobiColor.COLOR3,
        data: thisYearByCC.map((dd) => pastYearMap.get(dd.code) || 0),
      },
    ];

    this.saveHistory('xAxisData', 'series');
  }
}
